import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysInvoiceAiModel } from './queryKeys';

const zodDeleteInvoiceAiModelSenderEndpointSchema = z.tuple([
  z.literal('vadmin'),
  z.literal('invoice'),
  z.literal('ai_model_settings'),
  z.uuid(),
  z.literal('sender'),
  z.uuid(),
]);

type DeleteInvoiceAiModelSenderResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

/**
 * Deletes an invoice AI model sender.
 *
 * @param invoiceAiModelSettingsId - The ID of the invoice AI model settings.
 * @param senderId - The ID of the sender to delete.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/delete_invoice_ai_model_sender_vadmin_invoice_ai_model_settings__model_id__sender__sender_id__delete
 */
export const deleteInvoiceAiModelSender = async ({
  invoiceAiModelSettingsId,
  senderId,
}: {
  invoiceAiModelSettingsId: UUID;
  senderId: UUID;
}) => {
  try {
    const response = await vestigasApi
      .delete(
        ENDPOINT.INVOICE_AI_MODEL.SETTINGS.DELETE_SENDER(
          invoiceAiModelSettingsId,
          senderId,
        ),
        {
          validate: {
            pathSegments: zodDeleteInvoiceAiModelSenderEndpointSchema,
          },
        },
      )
      .json<DeleteInvoiceAiModelSenderResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error deleting invoice AI model sender',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error; // re-throw error so it can be handled higher up in the callstack.
  }
};

/**
 * Custom hook for deleting invoice AI model sender using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/delete_invoice_ai_model_sender_vadmin_invoice_ai_model_settings__model_id__sender__sender_id__delete
 */
export const useMutationDeleteInvoiceAiModelSender = (
  options?: UseMutationOptions<
    DeleteInvoiceAiModelSenderResponse,
    unknown,
    {
      invoiceAiModelSettingsId: UUID;
      senderId: UUID;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      invoiceAiModelSettingsId,
      senderId,
    }: {
      invoiceAiModelSettingsId: UUID;
      senderId: UUID;
    }) =>
      deleteInvoiceAiModelSender({
        invoiceAiModelSettingsId,
        senderId,
      }),
    onSettled(_data, _error, { invoiceAiModelSettingsId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceAiModel.getSettings(invoiceAiModelSettingsId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceAiModel.getAllSettings({}),
      });
    },
    ...options,
  });
};
